import { useState } from 'react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { AxiosError } from 'axios'
import { useAuth } from '../hooks/useAuth'

export default function RegisterPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [gender, setGender] = useState<'male' | 'female' | 'other' | 'prefer_not_to_say' | ''>('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { register } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Пароли не совпадают')
      return
    }

    if (password.length < 8) {
      setError('Пароль должен быть не менее 8 символов')
      return
    }

    setLoading(true)

    try {
      await register(
        email,
        password,
        name || undefined,
        birthDate || undefined,
        gender || undefined
      )
    } catch (err) {
      if (err instanceof AxiosError && err.response?.data) {
        const data = err.response.data
        // FastAPI validation errors come as array in detail
        if (Array.isArray(data.detail)) {
          // Get first validation error message
          const firstError = data.detail[0]
          setError(firstError?.msg || 'Ошибка валидации')
        } else if (typeof data.detail === 'string') {
          setError(data.detail)
        } else {
          setError('Ошибка регистрации')
        }
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Ошибка регистрации')
      }
    } finally {
      setLoading(false)
    }
  }

  // Password strength indicator - checks actual requirements
  const getPasswordStrength = () => {
    if (!password) return null

    const hasUpper = /[A-Z]/.test(password)
    const hasLower = /[a-z]/.test(password)
    const hasDigit = /\d/.test(password)
    const isLongEnough = password.length >= 8

    const checks = [hasUpper, hasLower, hasDigit, isLongEnough].filter(Boolean).length

    if (checks < 3) return { label: 'Слабый', color: 'bg-error', width: '33%' }
    if (checks < 4) return { label: 'Почти', color: 'bg-warning', width: '66%' }
    return { label: 'Надёжный', color: 'bg-success', width: '100%' }
  }

  const strength = getPasswordStrength()

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12 bg-gradient-calm">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-secondary-200 rounded-full blur-xl opacity-50" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-3xl flex items-center justify-center shadow-soft breathe">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-center text-3xl font-bold text-neutral-800">
          Создать аккаунт
        </h1>
        <p className="mt-2 text-center text-neutral-500">
          Начните путь к лучшему себе
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up">
        <div className="card-elevated py-8 px-6 sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-error-light text-error-dark p-4 rounded-2xl text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Имя <span className="text-neutral-400">(необязательно)</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Как к вам обращаться?"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="birthDate" className="block text-sm font-medium text-neutral-700 mb-1.5">
                  Дата рождения
                </label>
                <input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="input"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-neutral-700 mb-1.5">
                  Пол
                </label>
                <select
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value as typeof gender)}
                  className="input"
                >
                  <option value="">Не указывать</option>
                  <option value="male">Мужской</option>
                  <option value="female">Женский</option>
                  <option value="other">Другой</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Пароль
              </label>
              <input
                id="password"
                data-testid="register-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Минимум 8 символов"
              />
              {/* Password requirements checklist */}
              {password && (
                <div className="mt-2 space-y-1">
                  <p className={clsx(
                    'text-xs flex items-center gap-1.5',
                    password.length >= 8 ? 'text-success-dark' : 'text-neutral-400'
                  )}>
                    {password.length >= 8 ? (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    Минимум 8 символов
                  </p>
                  <p className={clsx(
                    'text-xs flex items-center gap-1.5',
                    /[A-Z]/.test(password) ? 'text-success-dark' : 'text-neutral-400'
                  )}>
                    {/[A-Z]/.test(password) ? (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    Заглавная буква (A-Z)
                  </p>
                  <p className={clsx(
                    'text-xs flex items-center gap-1.5',
                    /[a-z]/.test(password) ? 'text-success-dark' : 'text-neutral-400'
                  )}>
                    {/[a-z]/.test(password) ? (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    Строчная буква (a-z)
                  </p>
                  <p className={clsx(
                    'text-xs flex items-center gap-1.5',
                    /\d/.test(password) ? 'text-success-dark' : 'text-neutral-400'
                  )}>
                    {/\d/.test(password) ? (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    Цифра (0-9)
                  </p>
                </div>
              )}
              {/* Password strength indicator */}
              {strength && (
                <div className="mt-2">
                  <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full transition-all duration-300', strength.color)}
                      style={{ width: strength.width }}
                    />
                  </div>
                  <p className={clsx('text-xs mt-1', {
                    'text-error-dark': strength.label === 'Слабый',
                    'text-warning-dark': strength.label === 'Почти',
                    'text-success-dark': strength.label === 'Надёжный',
                  })}>
                    {strength.label === 'Почти' ? 'Почти готово' : `${strength.label} пароль`}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-1.5">
                Подтвердите пароль
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={clsx(
                  'input',
                  confirmPassword && password !== confirmPassword && 'border-error focus:border-error focus:ring-error/20',
                  confirmPassword && password === confirmPassword && password.length >= 8 && 'border-success focus:border-success focus:ring-success/20'
                )}
                placeholder="Повторите пароль"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-error-dark mt-1 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Пароли не совпадают
                </p>
              )}
              {confirmPassword && password === confirmPassword && password.length >= 8 && (
                <p className="text-xs text-success-dark mt-1 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Пароли совпадают
                </p>
              )}
            </div>

            <button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className={clsx(
                'btn-primary w-full text-base py-3.5',
                loading && 'opacity-70 cursor-wait'
              )}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Создаём аккаунт...
                </span>
              ) : (
                'Создать аккаунт'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-500">
            Уже есть аккаунт?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Войти
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-neutral-400">
            Создавая аккаунт, вы соглашаетесь с{' '}
            <a href="#" className="text-primary-600 hover:underline">
              условиями использования
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
